import { useState, useEffect, useCallback, useMemo } from "react";
import { COLOR_CODES } from "../../../common";
import FilterMenu from "../../../components/FilterMenu";
import Loading from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { SearchBox } from "../../../components/Search";
import { BrushStart } from "../../../models/chart_model";
import { RajasthanDiscomDrawal } from "../../../models/rajasthan";
import {
  getDemoRajasthanDiscomDrawlData,
  getDemoRajasthanDiscomDrawlForecastData,
  getRajasthanDiscomDrawlData,
  getRajasthanDiscomDrawlForecastData,
} from "../../../Rest_api/RestApi";
import { R_CHART_TYPES } from "../const";
import { GnaRuvnlFooter } from "../components";
import { DynamicDatePicker } from "../../../components/DatePicker";
import {
  convertToString,
  formatDateRange,
  getMonthFromDate,
} from "../../../extensions/date";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import { FilterTab } from "../../../components/Filters";
import { DataTabs, DEMAND_UNITS } from "./Data";
import { renderHourTick } from "../../../components/recharts/components";
import { generateDateRanges } from "../Generation/Data";
import { stringToConsistentColor } from "../../../extensions/string";
import Tooltip from "../../../components/tooltip";
import Papa, { Parser, ParseResult } from "papaparse";
import { DEFAULT_DATES } from "../../Hppc/Demand/Data";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "./SecondaryHeader";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import { useDispatch, useSelector } from "react-redux";
import { ExchangeColors } from "../Exchange3/FormatData";
import { AddDatesWithFileName } from "../../../helpers/helper";

const GROUP_BY = [
  {
    label: "Area",
    key: "area",
    values: ["area", "discom"],
  },
  {
    label: "Total",
    key: "total",
    values: ["total"],
  },
  {
    label: "Discom",
    key: "discom",
    values: ["discom"],
  },
];

const DISCOM = [
  {
    label: "Discom 1",
    key: "DISCOM1",
  },
  {
    label: "Discom 2",
    key: "DISCOM2",
  },
  {
    label: "Discom 3",
    key: "DISCOM3",
  },
];

let Intervals = [
  {
    label: "Time Slot",
    key: "time",
    values: ["block", "dateIndex"],
    isTime: true,
  },
  {
    label: "Date",
    key: "dateIndex",
    values: ["dateIndex"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    values: ["month"],
    isTime: false,
  },
];

const UNITS = DEMAND_UNITS;

export function RajasthanDrawalTrend() {
  const [TableData, setTableData] = useState<any[]>([]);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates
  );
  const uniqueDate = DefaultDates[activeTab].trendDateOptions;
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const [selectedUnit, setSelectedUnit] = useState(UNITS[0]);
  const [data, setData] = useState<any[]>([]);
  const [fileForecastData, setFileForecastData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [selectedDiscom, setSelectedDiscom] = useState<any[]>([]);
  const [uniqueArea, setUniqueArea] = useState<any[]>([]);
  const [selectedArea, setSelectedArea] = useState<any[]>([]);
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(R_CHART_TYPES[2]);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState([GROUP_BY[1]]);
  const [selectedInterval, setSelectedInterval] = useState(Intervals[0]);
  const [loading, setLoading] = useState(true);

  // New state to hold the uploaded forecast CSV file.

  // Handler for file upload—parses CSV with expected columns: date, block, area, predicted_demand.
  function handleForecastFile(e: React.ChangeEvent<HTMLInputElement>) {
    const forecastFile = e.target.files?.[0];
    // setForecastFile(forecastFile as any);
    e.target.value = ""; // reset file input
    let allData: any[] = [];
    setLoading(true);
    if (!forecastFile) return;
    Papa.parse(forecastFile, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      worker: true, // Process using a web worker
      chunk: (results: ParseResult<any>, parser: Parser) => {
        // Map over each row in the current chunk and transform the data.
        const chunkData = results.data.map((row: any) => ({
          // Use the CSV "date" as is (assumed to be in YYYY-MM-DD format)
          date: row.date,
          block: row.block,
          area: row.area,
          // Convert predicted_demand to number and assign as both mw and mwh (mwh = mw/4)
          predicted_demand: parseFloat(row.predicted_demand),
          mw: parseFloat(row.predicted_demand),
          mwh: parseFloat(row.predicted_demand) / 4,
          lu: parseFloat(row.predicted_demand) / 400,
          // Assign discom as constant "Total"
          discom: "Total",
          // Create additional fields for charting purposes
          // dateIndex: new Date(row.date).getDate().toString() + " Day",
          month: new Date(row.date).getMonth() + 1,
          // Optionally, set a label for the date range if needed
          // dates: row.date,
        }));
        // Accumulate the transformed data
        allData = allData.concat(chunkData);
      },
      complete: () => {
        // After all chunks are processed, update your state variables.
        setFileForecastData(allData);
        setUniqueBlock(Array.from(new Set(allData.map((item) => item.block))));
        setLoading(false);
      },
      error: (error: any) => {
        console.error("Error parsing forecast CSV:", error);
        setLoading(false);
      },
    }); // Optionally, you can parse immediately here or wait for _handlefetchData to use it.
  }

  // Modified fetch function: if a forecast file is provided, parse it; otherwise, use API fetch.
  const _handlefetchData = useCallback(async () => {
    if (DefaultDates[activeTab].defaultTrendDates === undefined) {
      return;
    }

    setLoading(true);

    const dates = DefaultDates[activeTab].defaultTrendDates;
    const promises = dates.map(async (date: any) => {
      const [startDate, endDate, model] = date.value.split(",");
      if (model) {
        const forecastedData = await getDemoRajasthanDiscomDrawlForecastData({
          startDate,
          endDate,
        });
        console.log(
          "🚀 ~ const_handlefetchData=useCallback ~ forecastedData:",
          forecastedData
        );
        return forecastedData.map((item) => ({ ...item, dates: date.value }));
      }
      const rangeData = await getDemoRajasthanDiscomDrawlData({
        startDate,
        endDate,
      });

      return rangeData.map((item) => ({
        ...item,
        dates: date.label,
      }));
    });
    const results = await Promise.all(promises);
    const newData = results.flat();
    console.log("🚀 ~ const_handlefetchData=useCallback ~ newData:", newData);
    setData(newData);
    setUniqueBlock(Array.from(new Set(newData.map((item) => item.block))));
    setLoading(false);
  }, [activeTab, DefaultDates]);

  async function getForecastData(startDate: string, endDate: string) {
    setLoading(true);

    // Filter the fileForecastData based on the date range.
    const fdata = fileForecastData.filter((item) => {
      const date_num = parseInt(item.date.replace(/-/g, ""));
      return (
        date_num >= parseInt(startDate.replace(/-/g, "")) &&
        date_num <= parseInt(endDate.replace(/-/g, ""))
      );
    });

    const uniqueDates = Array.from(new Set(fdata.map((item) => item.date)));

    // Transform the data to include additional fields.
    const final = fdata.map((item: any) => ({
      ...item,
      dateIndex: (uniqueDates.indexOf(item.date) + 1).toString() + " Day",
      dates: `${startDate} to ${endDate}`,
      block: item.block,
      month: getMonthFromDate(item.date),
    }));

    // Sort the data by date first and then by block.
    final.sort((a, b) => {
      // Compare the date strings.
      const dateComparison = a.date.localeCompare(b.date);
      if (dateComparison !== 0) {
        return dateComparison;
      }
      // If dates are equal, compare the block values.
      // Convert to number if blocks are numeric; otherwise, compare as strings.
      if (a.block < b.block) return -1;
      if (a.block > b.block) return 1;
      return 0;
    });

    return final;
  }

  function _handleGroupByChange(index: number) {
    // if (selectedArea.length === 0 && index == 0) return;
    setSelectedGroupKeys([GROUP_BY[index]]);
  }

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  useEffect(() => {
    let filteredData = data.map((item) => ({
      ...item,
      mwh: item.mw / 4,
    }));

    if (selectedDiscom.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedDiscom.includes(item.discom)
      );
    }
    setUniqueArea(Array.from(new Set(filteredData.map((item) => item.area))));

    if (selectedArea.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedArea.map((opt) => opt.value).includes(item.area)
      );
    }

    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedBlock.map((opt) => opt.value).includes(item.block)
      );
    }

    const par: ChartDataParams = {
      keys:
        selectedInterval.key === "dateIndex"
          ? ["dateIndex"]
          : selectedInterval.values,
      tableKeys: ["dateIndex", "block", "month"],
      groupBy: ["dates"].concat(
        selectedGroupKeys.map((item) => item.key.toLowerCase())
      ),
      volumeKey: "mwh",
      aggregationKeys: ["lu"],
      averageKeys: ["mw"],
      // reverseSort: ['dateIndex', 'block'],
      // averageGroupKeys: ["dates", "area", "discom"],
      averageDuplicationKeys: ["dateIndex", "block"],
      priceKey: "",
      data: filteredData,
    };
    const cData = FormatChartData(par);
    setTableData(cData.tableData || []);
    setChartData(cData.data);
    setChartKeys(cData.keys);
  }, [
    data,
    selectedGroupKeys,
    selectedInterval,
    selectedDiscom,
    selectedArea,
    selectedBlock,
  ]);

  function handleUnitChange(params: string) {
    setSelectedUnit(params);
  }

  function getLegends() {
    return chartKeys.map((key, index) => {
      const [dateRange, discomOrArea] = key.includes("_")
        ? key.split("_")
        : [key, ""];
      const aa = key.includes("Model") ? "Forecast" : "Demand";

      return {
        dataKey: key + "_" + selectedUnit.toLowerCase(),
        name: key.includes("to")
          ? `${key.split("_")[0]} ${discomOrArea} ${aa}`.replace(
              "undefined",
              ""
            )
          : `${formatDateRange(
              key.split("_")[0]
            )} ${discomOrArea} ${aa}`.replace("undefined", ""),

        legendColor: undefined,
        stroke: stringToConsistentColor(key),
        type: selectedChartType.value,
        stackId:
          selectedChartType.stackId !== undefined
            ? selectedChartType.stackId
            : undefined,
      };
    });
  }

  function summaryExtraKeys() {
    if (selectedInterval.isTime) {
      if (selectedBlock.length > 0) {
        if (selectedInterval.key === "month") {
          return ["month"];
        } else {
          return ["dateIndex", "block"];
        }
      } else {
        return ["dateIndex", "block"];
      }
    } else {
      return [selectedInterval.values[0]];
    }
  }

  function summaryExtraLabels() {
    if (selectedInterval.isTime) {
      if (selectedBlock.length > 0) {
        if (selectedInterval.key === "month") {
          return ["Month"];
        } else {
          return ["Date Index", "Block"];
        }
      } else {
        return ["Date Index", "Block"];
      }
    } else {
      return [selectedInterval.values[0]];
    }
  }

  const handlexDataKey = useCallback(() => {
    if (selectedInterval.key === "time") {
      if (selectedBlock.length > 0) {
        return "block";
      }
      return "block";
    } else {
      // if (selectedUnit !== DEMAND_UNITS[1]) {
      //   setSelectedUnit(DEMAND_UNITS[1]);
      // }
      return selectedInterval.key;
    }
  }, [selectedBlock, selectedInterval]);

  const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);

  const commonSummaryProps = {
    legends: getLegends() as any,
    data: chartData.map((item) => ({
      ...item,
      dateIndex:
        item.dateIndex && typeof item.dateIndex === "string"
          ? parseInt(item.dateIndex.replace("Day", ""))
          : item.dateIndex || 0,
    })),
    tableData: TableData.map((item) => ({
      ...item,
      dateIndex:
        item.dateIndex && typeof item.dateIndex === "string"
          ? parseInt(item.dateIndex.replace("Day", ""))
          : item.dateIndex || 0,
    })),
    unit: selectedUnit,
    extraKeys: summaryExtraKeys(),
    extraLabels: summaryExtraLabels(),
    filename: AddDatesWithFileName({
      fileName: "Restricted Demand Trend Analysis",
      dates: DefaultDates[activeTab].defaultTrendDates,
    }),
  };

  return (
    <div className="gas-page-container">
      {loading && <Loading />}
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="Interval"
            items={Intervals.map((item) => item.label)}
            selectedItems={[selectedInterval.label]}
            onSelect={(item, index) => setSelectedInterval(Intervals[index])}
          />
          <FilterMenu
            title="Unit"
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item, index) => handleUnitChange(item)}
          />
          <FilterMenu
            title="Group By"
            items={GROUP_BY.map((item) => item.label)}
            selectedItems={selectedGroupKeys.map((item) => item.label)}
            onSelect={(item, index) => _handleGroupByChange(index)}
          />
          <FilterMenu
            title="Discom"
            items={DISCOM.map((item) => item.label)}
            selectedItems={selectedDiscom}
            onSelect={(item, index) => {}}
            setSelectedItems={setSelectedDiscom}
          />
          <FilterMenu
            title="Chart Type"
            items={R_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) =>
              setSelectedChartType(R_CHART_TYPES[index])
            }
          />
          {/* New file upload input for forecast CSV */}
        </div>
      </NewFilterContainer>
      <MainPageContainer>
        <SecondaryHeader />
        <div className="gas-chart-container">
          <div className="gas-heading gap-x-10">
            <h2 className="chartHeading w-60">
              Restricted Demand ({selectedUnit})
            </h2>
            <div className="flex gap-x-6  justify-end">
              <SearchBox
                options={uniqueBlock.map((item) => ({
                  value: item,
                  label: item,
                }))}
                isMany={true}
                placeholder="Search Block"
                selectedOptions={selectedBlock}
                onChange={setSelectedBlock}
              />

              <div className="min-w-40">
                <SearchBox
                  options={uniqueArea.map((item) => ({
                    value: item,
                    label: item,
                  }))}
                  isMany={true}
                  placeholder="Search Area"
                  selectedOptions={selectedArea}
                  onChange={(v: any) => {
                    setSelectedArea(v);
                    if (selectedInterval.isTime && v.length === 0) {
                      setSelectedGroupKeys([GROUP_BY[1]]);
                    } else {
                      setSelectedGroupKeys([GROUP_BY[0]]);
                    }
                  }}
                />
              </div>
              <button
                onClick={() => {
                  if (dataTab.label === "Chart") {
                    downloadChartTableCSV(commonSummaryProps);
                  }
                  if (dataTab.label === "Summary") {
                    downloadSummaryCSV(commonSummaryProps);
                  }
                  if (dataTab.label === "Table") {
                    downloadChartTableCSV(commonSummaryProps);
                  }
                }}
                className="downloadButton"
              >
                Download Data
              </button>
            </div>
          </div>
          {dataTab.label === "Chart" && (
            <ReMixChart
              data={chartData}
              showBrush={chartData.length > 96 ? true : undefined}
              brushStart={BrushStart.Start}
              isTimeSlot={selectedInterval.isTime}
              onlyTitle={
                selectedBlock.length !== 0 &&
                selectedInterval.key == "dateIndex"
              }
              barCategoryGap={selectedInterval.isTime ? 2 : 40}
              unit={selectedUnit}
              yAxisLabel={selectedUnit}
              legends={getLegends()}
              xDataKey={xDataKey}
              xTick={
                selectedInterval.isTime && selectedBlock.length === 0
                  ? (value: any, index: any) => renderHourTick(value, 10)
                  : undefined
              }
              secondXDataKey={
                selectedInterval.isTime && selectedBlock.length > 0
                  ? "dateIndex"
                  : selectedInterval.values[1]
              }
            />
          )}
          {dataTab.label === "Summary" && (
            <LegendSummaryTable {...commonSummaryProps} />
          )}
          {dataTab.label === "Table" && <ChartTable {...commonSummaryProps} />}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-2 justify-between gap-5 ">
            <p className="gna-footer-text text-start col-span-1">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Demo Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
